import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import clsx from 'clsx';
import { UnregisterCallback } from 'history';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, NavLink as RouterLink, useHistory, withRouter } from 'react-router-dom';
import { useTextMessaging, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useResellerFromSubdomain } from '../../../../models/hooks/useResellerFromSubdomain';
import { useSurveyPermissions } from '../../../../models/hooks/useSurveyPermissions';
import { useEventLogging } from '../../../../models/Logging';
import { canViewContentCalendarAndCampaigns } from '../../../../models/UiUtils';
import { useAutomationsOnHoldQuery } from '../../../../queries';
import { useActionItemSummaryQuery } from '../../../../queries/ActionItem/useActionItemSummaryQuery';
import { ROUTES } from '../../../constants/routes';
import { TrialExpiringSoon } from '../../Billing/TrialExpiringSoon';
import { FlagIcon } from '../../svgs/icons/FlagIcon';
import { ResellerCompactLogoIcon } from '../../svgs/icons/ResellerCompactLogoIcon';
import { ResellerLogoIcon } from '../../svgs/icons/ResellerLogoIcon';
import { INavBarLink, NavBarLink } from '../NavBarLink';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	compactLayout?: boolean;
	header?: React.ReactNode;
	onRenderLogo?(compactLayout?: boolean): React.ReactNode;
}

function _NavBar({ className, compactLayout, header, onRenderLogo }: IProps) {
	const [automationsPending, setAutomationsPending] = React.useState<boolean>(false);

	const history = useHistory();
	const userSession = useUserSession();
	const textMessaging = useTextMessaging();
	const surveyPermissions = useSurveyPermissions();
	useEventLogging('Navbar');

	const automationsEnabled = userSession?.account?.features?.automation?.enabled ?? false;

	const { data: automationsOnHoldData } = useAutomationsOnHoldQuery({
		includeAll: userSession?.canSendOnBehalf,
		enabled: automationsEnabled,
	});
	const { data: actionItemSummary } = useActionItemSummaryQuery();
	const overdueActionItems = actionItemSummary?.overdue ?? 0;

	const checkForAutomations = React.useCallback(() => {
		if (automationsEnabled && automationsOnHoldData) {
			const automationsWithContacts = automationsOnHoldData.filter(x => x.count > 0);
			setAutomationsPending(automationsWithContacts.length > 0);
		}
	}, [automationsEnabled, automationsOnHoldData]);

	React.useEffect(() => {
		let unregisterHandle: UnregisterCallback = null;
		unregisterHandle = history.listen(() => checkForAutomations());
		checkForAutomations();
		return () => {
			if (unregisterHandle) {
				unregisterHandle();
			}
		};
	}, [checkForAutomations, history]);

	const isLimitedUser = userSession.userRole === 'limitedUser';
	// Limited users currently don't have a sidenav for Automations BUT they do have the read permission.
	// We need to keep the sidenav from displaying for them.
	const canViewAutomations =
		!!userSession.account.features.automation?.enabled &&
		userSession.featurePermissions?.automation?.read &&
		!isLimitedUser;
	const canViewTexting = Api.VmUtils.canViewTexting(userSession);
	const canViewCalendarAndCampaigns = canViewContentCalendarAndCampaigns(userSession);

	const boardsFeature = userSession.account.features.boards;
	const canViewVolunteers = boardsFeature?.volunteerBoard?.enabled ?? false;
	const canViewDataboards =
		boardsFeature?.opportunitiesEnabled ||
		boardsFeature?.donationsEnabled ||
		boardsFeature?.policiesEnabled ||
		boardsFeature?.matterBoard?.enabled ||
		boardsFeature?.meetingBoard?.enabled ||
		boardsFeature?.serviceJobBoard?.enabled ||
		canViewVolunteers ||
		false;

	const canViewSurveys = userSession.account.features?.surveys?.enabled && surveyPermissions.hasAnySurveyPermissions();
	const reseller = useResellerFromSubdomain(userSession?.account?.reseller);
	return (
		<nav className={`nav-bar ${clsx(css(styleSheet.nav, !compactLayout && styleSheet.navExpanded), className)}`}>
			{header || (
				<div className={css(styleSheet.header)}>
					{onRenderLogo?.(compactLayout) || (
						<RouterLink to='/dashboard'>
							{compactLayout ? (
								<ResellerCompactLogoIcon height={28} width={28} reseller={reseller} />
							) : (
								<ResellerLogoIcon className={css(styleSheet.mainLogo)} reseller={reseller} />
							)}
						</RouterLink>
					)}
				</div>
			)}
			<div className={css(styleSheet.links)}>
				<HappinessNavBarLink linkName='Dashboard' path='/dashboard' compactLayout={compactLayout} />
				{canViewCalendarAndCampaigns ? (
					<HappinessNavBarLink compactLayout={compactLayout} linkName='Campaigns' path='/campaigns' />
				) : null}
				{canViewCalendarAndCampaigns ? (
					<HappinessNavBarLink compactLayout={compactLayout} linkName='Content Calendar' path='/content-calendar' />
				) : null}

				{isLimitedUser ? (
					<HappinessNavBarLink compactLayout={compactLayout} linkName='Contacts' path='/contacts' />
				) : (
					<HappinessNavBarLink
						compactLayout={compactLayout}
						linkName='Contacts'
						path={['/contacts', '/contacts/companies', 'contacts/tags']}
					/>
				)}
				{canViewSurveys ? (
					<HappinessNavBarLink compactLayout={compactLayout} linkName='Surveys' path='/surveys' />
				) : null}
				{canViewTexting ? (
					<HappinessNavBarLink
						accessory={
							textMessaging?.totalUnreadMessages ? (
								<div
									className={css(
										compactLayout ? styleSheet.compactUnreadTextMessageCount : styleSheet.unreadTextMessageCount
									)}
								>
									{textMessaging.totalUnreadMessages}
								</div>
							) : null
						}
						compactLayout={compactLayout}
						linkName='Texting'
						path={['/texting', '/texting/registration']}
					/>
				) : null}
				<HappinessNavBarLink
					accessory={
						overdueActionItems > 0 ? (
							<div
								aria-label={`${overdueActionItems} overdue action items`}
								className={css(
									compactLayout ? styleSheet.compactUnreadTextMessageCount : styleSheet.unreadTextMessageCount
								)}
							>
								{overdueActionItems > 99 ? '99+' : overdueActionItems}
							</div>
						) : null
					}
					compactLayout={compactLayout}
					linkName='Notes & Actions'
					path={
						overdueActionItems > 0
							? ['/notes-action-items?activeTab=action-items', '/notes-action-items']
							: '/notes-action-items'
					}
				/>
				{canViewAutomations ? (
					<HappinessNavBarLink
						accessory={automationsPending ? <FlagIcon /> : null}
						compactLayout={compactLayout}
						linkName='Automations'
						path='/automations'
					/>
				) : null}
				{canViewDataboards ? (
					<HappinessNavBarLink compactLayout={compactLayout} linkName='Data Boards' path='/dataBoards' />
				) : null}
				{canViewVolunteers ? (
					<HappinessNavBarLink
						compactLayout={compactLayout}
						linkName='Volunteer Activities'
						path={ROUTES.volunteers.home}
					/>
				) : null}
				<HappinessNavBarLink compactLayout={compactLayout} linkName='Reporting' path={ROUTES.reporting.home} />
			</div>
			<TrialExpiringSoon compactLayout={compactLayout} userSession={userSession} />
		</nav>
	);
}

const NavBarAsObserver = observer(_NavBar);
export const NavBar = withRouter(NavBarAsObserver);

const HappinessNavBarLink: React.FC<Omit<INavBarLink, 'activeLinkStyles' | 'linkStyles'>> = ({
	compactLayout,
	...props
}) => (
	<NavBarLink
		activeLinkStyles={[styleSheet.linkActive, !compactLayout && styleSheet.expandedTabIndicator]}
		linkStyles={[styleSheet.link, compactLayout && styleSheet.linkCompact]}
		compactLayout={compactLayout}
		{...props}
	/>
);
